import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { KiteProgressIndicator } from '@kite/react-kite';
import { IAuthSessionContext } from './types/authSession';
import authContextDefault from './utils/authSessionDefault';
import { useUserIdle } from './hooks/useUserIdle';
import { useAccessToken } from './hooks/useAccessToken';

export const AuthSessionContext =
  createContext<IAuthSessionContext>(authContextDefault);

/**
 * Handles the management of a Auth Session
 */
export const AuthSessionContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loaderTitle, setLoaderTitle] = useState<string>('');

  const {
    isLoading: isAccessTokenLoading,
    accessToken,
    idToken,
    idTokenObj,
    legacyToken,
    isAuthenticating,
    isAuthenticated,
    isAuthError,
    signOut: accessTokenSignOut,
    refreshTokenIfExpired,
    authenticateViaEsso,
  } = useAccessToken();

  useEffect(() => {
    // Show Full Page Loading until everything is done loading
    setIsLoading(isAccessTokenLoading);
    if (isAuthenticating) {
      setLoaderTitle('Signing In');
    }
  }, [isAccessTokenLoading, isAuthenticating]);

  /** Called anytime a user is interacting with the site  */
  const handleOnUserInteraction = useCallback(() => {
    // if the access token hasn't been updated in awhile and user is still active, refresh token.
    refreshTokenIfExpired().catch(console.error);
  }, [refreshTokenIfExpired]);

  /**
   * Monitors user inactivity
   */
  const { showIdlePrompt, resetIdlePrompt, isUserIdleLogout } = useUserIdle({
    isAuthenticated,
    onUserInteraction: handleOnUserInteraction,
  });

  /**
   * Sign the user out and clear auth session
   */
  const signOut = useCallback(async () => {
    setIsLoading(true); // Show a full screen loading indicator
    setLoaderTitle('Signing Out');
    await accessTokenSignOut();
  }, [accessTokenSignOut]);

  useEffect(() => {
    if (isUserIdleLogout) {
      // The user has been inactive for too long, its time to automatically sign them out.
      setTimeout(() => {
        // give child components a couple seconds to tie up any loose ends before sign out.
        signOut().catch(console.error);
      }, 2000);
    }
  }, [isUserIdleLogout, signOut]);

  return (
    <AuthSessionContext.Provider
      value={useMemo(
        () => ({
          isAuthenticating,
          isAuthenticated,
          isAuthError,
          authToken: accessToken,
          idToken,
          idTokenObj,
          legacyToken,
          isUserIdleLogout,
          showIdlePrompt,
          resetIdlePrompt,
          signOut,
          authenticateViaEsso,
        }),
        [
          isAuthenticating,
          isAuthenticated,
          isAuthError,
          accessToken,
          idToken,
          idTokenObj,
          legacyToken,
          isUserIdleLogout,
          showIdlePrompt,
          resetIdlePrompt,
          signOut,
          authenticateViaEsso,
        ]
      )}
    >
      {!isLoading && children}
      {isLoading && (
        <div className="display-loader">
          <KiteProgressIndicator id="auth-session-loader" title={loaderTitle} />
        </div>
      )}
    </AuthSessionContext.Provider>
  );
};
